import type { ImageView } from '@vizit/api';
import { Asset } from '@vizit/molecules';
import { AssetGrid, RemoveImageFromCollectionDialog } from '@vizit/organisms';
import * as React from 'react';
import { useCollectionAssetGrid } from './collection-asset-grid.hooks';
import { useRemoveAssetFromCollection } from './collection-asset-grid.remove-asset.hook';
import { CollectionAsset } from './collection-asset-grid.sortable-asset';

interface CollectionAssetGridProps {
  /**
   * The ID of the portfolio
   */
  portfolioId: string;
  /**
   * The ID of the collection
   */
  collectionId: string;
  /**
   * Additional CSS classes to apply to the grid container
   */
  className?: string;
  /**
   * Callback when an asset is clicked
   */
  onNavigateToAsset: (id: string) => void;
  /**
   * Callback when total count changes (useful for displaying count in parent)
   * @param count - The total number of assets in the collection
   */
  onTotalCountChange?: (count: number) => void;
  /**
   * Whether to automatically calculate optimal initial load count
   * @default true
   */
  useOptimalInitialLoad?: boolean;
  /**
   * Number of items to load per page
   * @default 12
   */
  limit?: number;
}

/**
 * CollectionAssetGrid component that fetches and displays assets within a collection
 * Uses infinite loading pattern for better performance
 *
 * @param props - The component props
 */
const CollectionAssetGrid = React.memo(function CollectionAssetGrid(props: CollectionAssetGridProps) {
  const {
    portfolioId,
    collectionId,
    className,
    onNavigateToAsset,
    onTotalCountChange,
    useOptimalInitialLoad = true,
    limit = 12,
  } = props;

  const { assets, totalCount, isLoading, hasNextPage, isFetchingNextPage, sentinelRef, loadingCount } =
    useCollectionAssetGrid({
      portfolioId,
      collectionId,
      limit,
      useOptimalInitialLoad,
    });

  const { isOpen, imageIdToRemove, onOpenChange, onRemoveImageDialogOpen, onRemove } = useRemoveAssetFromCollection({
    portfolioId,
    collectionId,
  });

  React.useEffect(() => {
    onTotalCountChange?.(totalCount);
  }, [totalCount, onTotalCountChange]);

  return (
    <>
      <AssetGrid
        showCount={false}
        loadingCount={loadingCount}
        totalCount={totalCount}
        isLoading={isLoading}
        className={className}
      >
        {assets.map((asset: ImageView) => (
          <CollectionAsset
            key={asset.id}
            image={asset}
            onAssetClick={onNavigateToAsset}
            onRemoveImageDialogOpen={onRemoveImageDialogOpen}
          />
        ))}

        {/* Show loading assets when fetching next page */}
        {isFetchingNextPage && loadingCount
          ? Array.from({ length: loadingCount }, (_, index) => {
              const skeletonId = `collection-asset-skeleton-next-${index}`;
              return (
                <Asset key={skeletonId} isLoading showScoreBar>
                  <Asset.Image name="Loading..." src={null} />
                </Asset>
              );
            })
          : null}
      </AssetGrid>

      {/* Sentinel element for infinite scroll detection */}
      {hasNextPage && <div ref={sentinelRef} className="h-4 w-full" aria-label="Loading more assets" />}

      <RemoveImageFromCollectionDialog
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        onRemove={onRemove}
        imageId={imageIdToRemove}
      />
    </>
  );
});

export { CollectionAssetGrid };
export type { CollectionAssetGridProps };
